import { Link } from 'react-router-dom';
import { Alert, Button, Drawer, Empty, Spin, Tag, Typography } from 'antd';
import { useAnomalies } from '../Monitoring/anomalies/useAnomalies';
import EventChart from '../Monitoring/anomalies/Chart';
import { resolveLabel } from '../Monitoring/anomalies/helpers';
import type { MonitoringOverview } from '../Monitoring/types';
import {
  getAnomalyPath,
  sensorTypeLabel,
  supportsAnomalies,
  typeSetup,
  type FleetDevice,
} from './model';
import { formatDate, reviewLabels, statusLabels } from './presentation';
import type { SourceState } from './FleetOverview';
import '../Monitoring/anomalies/anomalies.css';
import DeviceWorkspace, {
  type DeviceWorkspaceView,
} from '../Monitoring/UnifiedAlerts/DeviceWorkspace';

function DeviceAnomalies({ device }: { device: FleetDevice }) {
  const { events, labels, query, scan } = useAnomalies({
    orgId: device.orgId,
    sensorId: device.sensorId,
  });
  const scanOld = Boolean(
    scan &&
      (!scan.lastCompletedAt ||
        Date.now() - scan.lastCompletedAt > 3 * 60 * 60 * 1000 ||
        (scan.lastFailedAt || 0) > scan.lastCompletedAt)
  );
  return (
    <section className="fleet-detail-section">
      <div className="fleet-section-heading">
        <h3>Afvigelser</h3>
        <Link to={getAnomalyPath(device)}>Åbn vurderinger →</Link>
      </div>
      <p className="fleet-muted">Målinger og vurderinger for denne enhed, nyeste først.</p>
      <p className="fleet-muted">
        Seneste analyse: {formatDate(scan?.lastCompletedAt)}{' '}
        <Button size="small" type="link" loading={query.isFetching} onClick={() => query.refetch()}>
          Opdatér afvigelser
        </Button>
      </p>
      {(scanOld || scan?.control?.enabled === false) && (
        <Alert
          showIcon
          type="warning"
          message={
            scan?.control?.enabled === false
              ? 'Afvigelsesanalysen er sat på pause'
              : 'Afvigelsesanalysen er ikke opdateret'
          }
          description="Resultaterne afspejler den seneste analyse."
        />
      )}
      {query.isPending && (
        <Spin tip="Henter enhedens afvigelser…">
          <div className="fleet-loader" />
        </Spin>
      )}
      {query.isError && (
        <Alert
          type="warning"
          showIcon
          message="Afvigelser kunne ikke hentes"
          action={<Button onClick={() => query.refetch()}>Prøv igen</Button>}
        />
      )}
      {!query.isPending && !query.isError && !events.length && (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Ingen registrerede afvigelser for enheden"
        />
      )}
      {events.slice(0, 3).map((event, index) => (
        <article key={event.id} className="fleet-anomaly-card">
          <div className="fleet-section-heading">
            <strong>{event.title}</strong>
            <Tag
              color={
                event.review.status === 'reviewed'
                  ? 'green'
                  : event.review.status === 'investigating'
                  ? 'gold'
                  : 'blue'
              }
            >
              {reviewLabels[event.review.status]}
            </Tag>
          </div>
          <small className="fleet-muted">{formatDate(event.occurredAt)}</small>
          {index === 0 && <EventChart event={event} />}
          <p>{event.description}</p>
          {event.review.labelId && (
            <Tag>{resolveLabel(event.review.labelId, labels)?.name || 'Ukendt label'}</Tag>
          )}
          {event.review.note && <blockquote>{event.review.note}</blockquote>}
        </article>
      ))}
      {(events.length > 3 || query.hasNextPage) && (
        <p className="fleet-muted">
          Viser de seneste {Math.min(3, events.length)} af {events.length}
          {query.hasNextPage ? '+' : ''} hændelser. Se resten under vurderinger.
        </p>
      )}
    </section>
  );
}

export default function DeviceOverviewDrawer({
  device,
  monitoring,
  monitoringState,
  view,
  onViewChange,
  onClose,
}: {
  device: FleetDevice;
  monitoring?: MonitoringOverview;
  monitoringState: SourceState;
  view?: DeviceWorkspaceView;
  onViewChange?: (view: DeviceWorkspaceView) => void;
  onClose: () => void;
}) {
  const incidents = (monitoring?.incidents || []).filter(
    (incident) => incident.orgId === device.orgId && incident.sensorId === device.sensorId
  );
  return (
    <Drawer
      open
      onClose={onClose}
      width={780}
      extra={device.detailPath ? <Link to={device.detailPath}>Åbn sensorside →</Link> : undefined}
      rootClassName="fleet-drawer"
      title={
        <div>
          <span className="fleet-eyebrow">AKS INTERN</span>
          <div>{device.sensorName}</div>
          <small className="fleet-muted">
            {device.orgName} · {sensorTypeLabel(device.type)}
          </small>
        </div>
      }
    >
      <DeviceWorkspace
        showIdentity={false}
        orgId={device.orgId}
        sensorId={device.sensorId}
        view={view}
        onViewChange={onViewChange}
        statusContent={(ownership) => {
          const unifiedActive = ownership === 'active';
          const existingSetup = ownership === 'legacy';
          return (
            <>
              <details className="fleet-detail-section fleet-technical-details">
                <summary>Tekniske oplysninger</summary>
                <p>{typeSetup(device.type)}</p>
                <Tag>{statusLabels[device.status]}</Tag>
                <dl className="fleet-details">
                  <div>
                    <dt>Device-ID</dt>
                    <dd>{device.deviceId || 'Ikke registreret'}</dd>
                  </div>
                  <div>
                    <dt>Sensor-ID</dt>
                    <dd>
                      <Typography.Text copyable>{device.sensorId}</Typography.Text>
                    </dd>
                  </div>
                  <div>
                    <dt>Kunde-ID</dt>
                    <dd>
                      <Typography.Text copyable>{device.orgId}</Typography.Text>
                    </dd>
                  </div>
                </dl>
              </details>
              <details className="fleet-detail-section fleet-historical-alarms">
                <summary>{unifiedActive ? 'Tidligere alarmer' : 'Eksisterende alarmer'}</summary>
                <p className="fleet-muted">
                  {unifiedActive
                    ? 'Historik fra den tidligere alarmovervågning. Nye hændelser findes under Hændelser.'
                    : existingSetup
                    ? 'Enheden bruger fortsat den eksisterende alarmopsætning. Gå til Indstillinger for at gennemgå og overføre den.'
                    : 'Det er ikke bekræftet, hvilken alarmopsætning enheden bruger. Eksisterende alarmer kan stadig være aktive. Gå til Indstillinger for at kontrollere opsætningen.'}
                </p>
                <div className="fleet-section-heading">
                  <h3>
                    {unifiedActive ? 'Tidligere alarmovervågning' : 'Eksisterende alarmovervågning'}
                  </h3>
                  {device.monitoring && !monitoringState.error && (
                    <Tag color={device.monitoring.activeIncidentCount ? 'red' : undefined}>
                      {device.monitoring.activeIncidentCount} åbne
                    </Tag>
                  )}
                </div>
                <p className="fleet-muted">
                  Senest vurderet: {formatDate(monitoring?.summary.lastEvaluatedAt)}
                </p>
                {monitoring &&
                  (!monitoring.summary.lastEvaluatedAt ||
                    Date.now() - monitoring.summary.lastEvaluatedAt.getTime() > 60 * 60 * 1000) && (
                    <Alert
                      type="warning"
                      showIcon
                      message="Overvågningens vurdering er ikke opdateret"
                      description="Alarmstatus bekræfter ikke enhedens aktuelle tilstand."
                    />
                  )}
                {monitoringState.loading && <p role="status">Henter overvågning…</p>}
                {monitoringState.error && (
                  <Alert
                    type="warning"
                    message="Alarmstatus kunne ikke opdateres"
                    description="Eventuelle hændelser nedenfor er fra den seneste hentning."
                  />
                )}
                {!monitoringState.loading && !monitoringState.error && !device.monitoring && (
                  <p className="fleet-muted">
                    Ingen vurderinger fra denne alarmovervågning er indlæst for enheden.
                  </p>
                )}
                <div className="fleet-incidents">
                  {incidents.map((incident) => (
                    <article key={incident.fingerprint}>
                      <div className="fleet-section-heading">
                        <strong>{incident.summary}</strong>
                        <Tag color={incident.status === 'active' ? 'red' : undefined}>
                          {incident.ruleCode === 'GSM_CUSTOM_ALERT'
                            ? 'SMS-hændelse'
                            : incident.status === 'active'
                            ? 'Åben'
                            : 'Afsluttet'}
                        </Tag>
                      </div>
                      <p>{incident.details}</p>
                      <small>
                        Først registreret {formatDate(incident.firstOpenedAt)} · senest set{' '}
                        {formatDate(incident.lastObservedAt)}
                      </small>
                      {incident.ruleCode === 'GSM_CUSTOM_ALERT' && (
                        <p className="fleet-muted">
                          Registreret besked. Det er ikke bekræftet, at en eventuel fejl er løst.
                        </p>
                      )}
                      {incident.resolutionSummary && <p>{incident.resolutionSummary}</p>}
                    </article>
                  ))}
                </div>
                {monitoring && (
                  <p className="fleet-muted">
                    {!incidents.length
                      ? 'Ingen hændelser for enheden blandt de seneste resultater. '
                      : ''}
                    Udtræk fra de seneste 100 opdaterede driftsalarmer på tværs af enheder. Det er
                    ikke en komplet historik.
                  </p>
                )}
              </details>
              {supportsAnomalies(device.type) ? (
                <DeviceAnomalies key={device.key} device={device} />
              ) : (
                <section className="fleet-detail-section">
                  <h3>Afvigelser</h3>
                  <p className="fleet-muted">
                    Afvigelsesanalysen omfatter FLOW- og GSM-anlæg. Denne enhedstype analyseres
                    ikke.
                  </p>
                </section>
              )}
            </>
          );
        }}
      />
    </Drawer>
  );
}
